/**
 * Frontend JavaScript for advanced group controls.
 * Handles linked groups, sticky positioning, and scroll behaviors.
 *
 * @package OllieUIHelpers
 */

( function() {
	'use strict';

	// Constants
	const MOBILE_BREAKPOINT = 600;
	const SCROLL_THRESHOLD = 5;
	const SCROLL_TOP_THRESHOLD = 100;

	/**
	 * Initialize linked blocks (groups and columns) with expanded clickable area.
	 * Uses event delegation to support dynamically loaded content (AJAX, filters, etc.).
	 */
	function initLinkedGroups() {
		document.addEventListener( 'click', ( event ) => {
			// Find if the click was inside a linked block
			const linkedBlock = event.target.closest( '.wp-block-group.is-linked, .wp-block-column.is-linked, .wp-block-cover.is-linked' );

			if ( ! linkedBlock ) {
				return;
			}

			const clickableElem = linkedBlock.querySelector( '[data-expand-click-area]' );

			if ( ! clickableElem ) {
				return;
			}

			// Don't intercept if clicking the link itself or an interactive element
			if ( event.target === clickableElem || event.target.closest( 'a, button, input, select, textarea, [role="button"]' ) ) {
				return;
			}

			// Forward the click to the link element
			event.preventDefault();
			clickableElem.click();
		} );
	}

	/**
	 * Apply sticky offset to parent header elements
	 */
	function initParentHeaderStickyOffset() {
		const stickyGroups = document.querySelectorAll( 'header > .wp-block-group.is-position-sticky' );

		if ( stickyGroups.length === 0 ) {
			return;
		}

		// Track resize handlers to avoid duplicates
		const resizeHandlers = new WeakMap();

		stickyGroups.forEach( ( group ) => {
			const header = group.parentElement;

			if ( !header || header.tagName.toLowerCase() !== 'header' ) {
				return;
			}

			// Skip if the group has scroll-up behavior (it handles its own positioning)
			if ( group.getAttribute( 'data-sticky-on-scroll-up' ) === 'true' ) {
				return;
			}

			const offset = group.getAttribute( 'data-sticky-offset' ) || '0px';
			const zIndex = group.getAttribute( 'data-sticky-z-index' );
			const hasUnstickMobile = group.classList.contains( 'ollie-unstick-mobile' );

			// Apply sticky styles to the header
			const applyHeaderStyles = ( isMobile = false ) => {
				if ( isMobile && hasUnstickMobile ) {
					header.style.position = 'relative';
					header.style.top = 'auto';
					header.style.zIndex = '';
				} else {
					header.style.position = 'sticky';

					// Apply z-index: use custom value if set, otherwise default to 100
					if ( zIndex && zIndex !== '0' ) {
						header.style.zIndex = zIndex;
					} else {
						header.style.zIndex = '100';
					}

					// Use WordPress admin bar CSS variable for consistent handling
					if ( offset && offset !== '0px' && offset !== '0' ) {
						header.style.top = `calc(${offset} + var(--wp-admin--admin-bar--height, 0px))`;
					} else {
						header.style.top = 'calc(0px + var(--wp-admin--admin-bar--height, 0px))';
					}
				}
			};

			// Initial application
			applyHeaderStyles( window.innerWidth <= MOBILE_BREAKPOINT );

			// Handle responsive behavior if needed
			if ( hasUnstickMobile ) {
				// Avoid duplicate listeners using WeakMap
				if ( !resizeHandlers.has( group ) ) {
					const handleResize = () => {
						applyHeaderStyles( window.innerWidth <= MOBILE_BREAKPOINT );
					};
					
					// Debounce resize handler for better performance
					let resizeTimer;
					const debouncedResize = () => {
						clearTimeout( resizeTimer );
						resizeTimer = setTimeout( handleResize, 150 );
					};
					
					window.addEventListener( 'resize', debouncedResize );
					resizeHandlers.set( group, debouncedResize );
				}
			}
		} );
	}

	/**
	 * Initialize sticky scroll behavior for group blocks
	 */
	function initStickyScrollBehavior() {
		const stickyGroups = document.querySelectorAll( '.wp-block-group[data-sticky-on-scroll-up="true"]' );

		if ( stickyGroups.length === 0 ) {
			return;
		}

		// Convert NodeList to Array for better performance
		const groupsArray = Array.from( stickyGroups );

		// Find parent .site-header elements for each sticky group
		const siteHeaders = new Set();
		groupsArray.forEach( group => {
			const siteHeader = group.closest( '.site-header' );
			if ( siteHeader ) {
				siteHeaders.add( siteHeader );
			}
		} );

		// State management
		let lastScrollTop = 0;
		let ticking = false;
		let isHidden = false;

		/**
		 * Update sticky state based on scroll direction
		 */
		function updateStickyState() {
			const currentScrollTop = window.pageYOffset || document.documentElement.scrollTop;
			const scrollDiff = Math.abs( currentScrollTop - lastScrollTop );

			// Skip minor scroll movements
			if ( scrollDiff < SCROLL_THRESHOLD && currentScrollTop > 0 ) {
				return;
			}

			const isScrollingUp = currentScrollTop < lastScrollTop;
			const isAtTop = currentScrollTop <= SCROLL_TOP_THRESHOLD;
			const shouldShow = isScrollingUp || isAtTop;

			// Only update DOM if state changes
			if ( shouldShow !== !isHidden ) {
				if ( shouldShow ) {
					// Restore z-index immediately so header is visible and clickable during slide-down
					siteHeaders.forEach( header => {
						header.style.zIndex = '';
					} );
					groupsArray.forEach( group => {
						group.classList.add( 'is-sticky-active' );
						group.classList.remove( 'is-sticky-hidden' );
					} );
					isHidden = false;
				} else if ( currentScrollTop > SCROLL_TOP_THRESHOLD ) {
					groupsArray.forEach( group => {
						group.classList.remove( 'is-sticky-active' );
						group.classList.add( 'is-sticky-hidden' );
					} );
					isHidden = true;
				}
			}

			lastScrollTop = Math.max( 0, currentScrollTop );
		}

		/**
		 * After the slide-up transform finishes, drop the site-header z-index
		 * so users can click through the empty space it leaves behind.
		 */
		groupsArray.forEach( group => {
			group.addEventListener( 'transitionend', ( event ) => {
				if ( event.propertyName !== 'transform' ) {
					return;
				}

				if ( group.classList.contains( 'is-sticky-hidden' ) ) {
					siteHeaders.forEach( header => {
						header.style.zIndex = '-1';
					} );
				}
			} );
		} );

		/**
		 * Handle scroll event with RAF throttling
		 */
		function handleScroll() {
			if ( !ticking ) {
				window.requestAnimationFrame( () => {
					updateStickyState();
					ticking = false;
				} );
				ticking = true;
			}
		}

		// Add scroll listener with passive flag for better performance
		window.addEventListener( 'scroll', handleScroll, { passive: true } );

		// Initialize state
		updateStickyState();
	}

	/**
	 * Initialize all advanced group features
	 */
	function init() {
		initLinkedGroups();
		initParentHeaderStickyOffset();
		initStickyScrollBehavior();
	}

	// Initialize when DOM is ready
	if ( document.readyState === 'loading' ) {
		document.addEventListener( 'DOMContentLoaded', init );
	} else {
		init();
	}

} )();
